import React, { useState, useContext, useRef, useLayoutEffect } from 'react';
import { Typography, Switch, Paper } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axios from 'axios';
import API_URL, {
  ACCME_PARS_URL,
  CEBROKER_URL,
  STATE_LICENSING_BOARDS_URL,
  ACCREDITATION_CONSENT_SITES_ARTICLE,
  isAccreditationSiteEnabled,
} from '../config/config';
import { contactContext } from '../context/contactContext';
import { Modal as PARSModal } from './CMEProfileManagement/ModalPopup';
import '../styles/learner-profile.css';

const PARSConsentSection: React.FC = () => {
  const { contactObject, updatePARSConsent } = useContext(contactContext);
  const contactId = contactObject.Contact?.contactid;
  const parsConsentAccepted = contactObject.Contact?.PARSConsentAccepted;

  const [modalOpen, setModalOpen] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  // Opens above the icon by default (original behavior); flips below only if that would
  // render off the top of the viewport, so the box is never cut off on shorter screens.
  const [openUpward, setOpenUpward] = useState(true);
  const tooltipRef = useRef<HTMLDivElement>(null);

  //const hasAttested = parsConsentAccepted !== null && parsConsentAccepted !== undefined;
  const isOn = parsConsentAccepted === true;

  useLayoutEffect(() => {
    if (!showInfo) return;
    const el = tooltipRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.top < 0) {
      setOpenUpward(false);
    }
  }, [showInfo]);

  const saveConsent = async (accepted: boolean) => {
    updatePARSConsent(accepted);
    try {
      await axios.patch(`${API_URL}/cmecontacts/${contactId}/parsconsent`, { PARSConsentAccepted: accepted });
    } catch (err) {
      console.error('Error saving PARS consent:', err);
    }
  };

  const handleToggleClick = () => {
    // Turning OFF (isOn is currently true, user is switching to No) — no confirmation modal,
    // no checkbox validation, just save directly. Turning ON still goes through the modal
    // (requires the checkbox to be checked) exactly as before.
    if (isOn) {
      saveConsent(false);
    } else {
      setModalOpen(true);
    }
  };

  const handleAccept = async () => { setModalOpen(false); await saveConsent(true); };
  const handleDecline = async () => { setModalOpen(false); await saveConsent(false); };
  const handleClose = () => setModalOpen(false);

  return (
    <div className="pars-section">
      <div className="pars-section-header">
        <Typography className="pars-section-title">
          Submit to State Licensing Boards
        </Typography>

        <div
          className="pars-info-wrapper"
          onMouseEnter={() => { setOpenUpward(true); setShowInfo(true); }}
          onMouseLeave={() => setShowInfo(false)}
        >
          <span className="pars-info-icon">
            <InfoOutlinedIcon fontSize="small" />
          </span>

          {showInfo && (
            <>
            <div className={`pars-info-bridge${openUpward ? '' : ' pars-info-bridge--below'}`} />
            <Paper
              elevation={4}
              ref={tooltipRef}
              className={`pars-info-tooltip${openUpward ? '' : ' pars-info-tooltip--below'}`}
            >
              <div className="pars-info-tooltip-inner">
                <InfoOutlinedIcon className="pars-info-tooltip-icon" />
                <ul className="pars-info-tooltip-list">
                  {/* One bullet per live accreditation site — a site commented out of
                      ACCREDITATION_SITES drops its bullet, the other's wording is untouched. */}
                  {isAccreditationSiteEnabled('PARS') && (
                  <li className="pars-info-tooltip-item">
                    <Typography variant="body2" className="pars-info-tooltip-text">
                      <a
                        href={ACCME_PARS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pars-info-tooltip-link"
                      >
                        The ACCME and State Medical Licensing Boards Collaboration
                      </a>
                      {' '}enables accredited CME providers to report physicians' CME credit details and make that
                      information available to participating state boards via ACCME's Program and Activity Reporting System.
                    </Typography>
                  </li>
                  )}
                  {isAccreditationSiteEnabled('CEBROKER') && (
                  <li className="pars-info-tooltip-item">
                    <Typography variant="body2" className="pars-info-tooltip-text">
                      CE Broker enables accredited providers to report physicians' CME credit details and make that
                      information available to participating state boards via the{' '}
                      <a
                        href={CEBROKER_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pars-info-tooltip-link"
                      >
                        CE Broker website
                      </a>.
                    </Typography>
                  </li>
                  )}
                </ul>
              </div>
            </Paper>
            </>
          )}
        </div>
      </div>

      <div className="pars-toggle-row">
        <span className="pars-toggle-switch">
          <Switch
            checked={isOn}
            onChange={handleToggleClick}
            //disabled={hasAttested}
            color="primary"
          />
        </span>
        <Typography variant="body2" className="pars-toggle-text">
          {isOn ? (
            <>
              I authorize ACEP to submit my credits to my state licensing boards via {ACCREDITATION_CONSENT_SITES_ARTICLE}.{' '}
              <a
                href={STATE_LICENSING_BOARDS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="pars-toggle-link"
              >
                View participating boards.
              </a>
            </>
          ) : (
            `Do NOT submit my credits to my state licensing boards via ${ACCREDITATION_CONSENT_SITES_ARTICLE}.`
          )}
        </Typography>
      </div>

      {modalOpen && (() => {
        const M = PARSModal as any;
        return (
          <M
            modalFor="PARSConsent"
            modalHeaderText="Submit to State Licensing Boards?"
            closeModal={handleClose}
            onSubmit={handleAccept}
            closeModalCustomClick={handleDecline}
            onClickOutside={() => {}}
            onKeyDown={() => {}}
            modalRef={null}
            buttonRef={null}
          />
        );
      })()}
    </div>
  );
};

export default PARSConsentSection;
